import { useCallback, useMemo, useState } from "react";
import { getYear } from "@/utils/date.util";
import { formatCurrency } from "@/utils/ranges.util";
import { CompanyDetailsFundingEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyDetailsFundingEntryDto";
import { Trans, useTranslation } from "react-i18next";
import { HiMagnifyingGlass } from "react-icons/hi2";

import { useCompanyFundings } from "@/hooks/api/company/useCompanyFundings";
import BarChartMultiple from "@/components/molecules/charts/BarChartMultiple";
import FeaturedText from "@/components/molecules/FeaturedText";
import InfiniteScrollListSearchable from "@/components/organisms/InfiniteScrollListSearchable";

type CompanyDetailFundingsProps = {
  id: string;
};

const CompanyDetailFundings = ({ id }: CompanyDetailFundingsProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState<string>("");

  /** Use useCompanyFundings two times, so chart data does not change when we query results **/
  const { data: tableData, isLoading: tableIsLoading } = useCompanyFundings({
    id,
    enabled: !!id,
    query,
  });

  const { data: chartData, isLoading: chartIsLoading } = useCompanyFundings({
    id,
    enabled: !!id,
    query: "",
  });

  const groupData = useCallback((data?: CompanyDetailsFundingEntryDto[]) => {
    if (!data) return [];

    const grouped = data
      .filter((f) => !!f.date)
      .reduce((acc: Record<string, any[]>, funding) => {
        const year = getYear(funding.date!);
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(funding);
        return acc;
      }, {});

    return Object.entries(grouped)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([year, fundings]) => ({
        year,
        fundings,
      }));
  }, []);

  const groupedChartData = useMemo(() => groupData(chartData), [chartData, groupData]);

  // If all fundings are null or 0 do not show the chart
  const chartIsShown = useMemo(() => {
    if (chartIsLoading || groupedChartData.length === 0) return false;
    return !groupedChartData.every((item) =>
      item.fundings.every((funding) => funding.amount === null || funding.amount === 0),
    );
  }, [groupedChartData, chartIsLoading]);

  const flatTableData = useMemo(
    () => groupData(tableData).flatMap((item) => item.fundings) ?? [],
    [tableData, groupData],
  );

  // TODO Fill unknown data with a grey bar -> too complicated -> dont show or improvement

  return groupedChartData.length === 0 && query === "" && !chartIsLoading ? (
    <div className={"py-10"}>
      <FeaturedText
        heading={t("search.noResults.title")}
        body={t("company.detail.overview.insights.funding.noData")}
        size={"lg"}
        featuredIcon={{
          icon: HiMagnifyingGlass,
          color: "primary",
          size: "xl",
        }}
      />
    </div>
  ) : (
    <>
      {chartIsLoading ? (
        <div className={"py-7"}>
          <div className={"mb-1.5 h-6 w-[30%] animate-pulse rounded-full bg-gray-300"} />

          <div className={"h-5 w-3/5 animate-pulse rounded-full bg-gray-300"} />

          <div className={"mt-6 h-48 w-full animate-pulse rounded-md bg-gray-300"} />
        </div>
      ) : (
        chartIsShown && (
          <>
            <div className={"py-7"}>
              <h3 className={"mb-1.5 font-semibold text-primary"}>
                {t("company.detail.overview.insights.funding.title")}
              </h3>
              <p className={"text-sm text-tertiary"}>{t("company.detail.overview.insights.funding.description")}</p>
            </div>

            <BarChartMultiple chartData={groupedChartData} />
          </>
        )
      )}

      <InfiniteScrollListSearchable
        title={t("company.detail.overview.insights.funding.details")}
        onSearch={setQuery}
        items={flatTableData}
        isLoading={tableIsLoading}
        containerClassName={"grid gap-6"}
        outerClassName={"py-7"}
        noQueryResultsNode={
          <div className={"py-10"}>
            <FeaturedText
              heading={t("search.noResults.title")}
              body={
                <Trans
                  i18nKey={"company.detail.overview.insights.funding.noDataQuery"}
                  values={{ query }}
                  components={{ bold: <strong /> }}
                />
              }
              size={"lg"}
              featuredIcon={{
                icon: HiMagnifyingGlass,
                color: "primary",
                size: "xl",
              }}
            />
          </div>
        }
        renderItem={(funding) => (
          <div key={funding.id}>
            <h4 className={"line-clamp-1 text-sm font-medium text-primary"}>
              {funding.amount ? formatCurrency(funding.amount) : t("company.detail.overview.insights.funding.unknown")}
            </h4>
            {funding.type && <p className={"mt-2 text-sm font-medium text-quinary"}>{funding.type}</p>}
            {funding.date && (
              <p className={"mt-1 text-xs text-quinary"}>{new Date(funding.date).toLocaleDateString()}</p>
            )}
          </div>
        )}
        renderLoading={Array.from({ length: 5 }).map((_, index) => (
          <div key={index}>
            <div className={"mb-3 h-5 w-20 animate-pulse rounded-md bg-gray-300"} />
            <div className={"mb-2 h-5 w-28 animate-pulse rounded-full bg-gray-300"} />
            <div className={"h-5 w-24 animate-pulse rounded-full bg-gray-300"} />
          </div>
        ))}
      />
    </>
  );
};

export default CompanyDetailFundings;
